import { useState, type ReactNode } from 'react';
import { CalendarClock, CalendarX2, CalendarOff, CheckCircle2, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TaskListItemDetailed } from './TaskListItemDetailed';
import { isTaskOverdue, type TaskClaimSummary } from '@/lib/task-filters';
import type { ClaimTask } from '@/api/generated/client';

interface TaskListViewProps {
  /** Tasks to display, expected to already be filtered and sorted by due date. */
  tasks: ClaimTask[];
  claimsMap: Map<number, TaskClaimSummary>;
  onTaskComplete?: (taskId: number, claimId: number) => void;
  onTaskEdit?: (task: ClaimTask) => void;
}

interface TaskGroupProps {
  title: string;
  tasks: ClaimTask[];
  claimsMap: Map<number, TaskClaimSummary>;
  icon: ReactNode;
  badgeClassName?: string;
  testId: string;
  /** When true, the group header can be clicked to collapse/expand the task list. */
  collapsible?: boolean;
  onTaskComplete?: (taskId: number, claimId: number) => void;
  onTaskEdit?: (task: ClaimTask) => void;
}

function TaskGroup({ title, tasks, claimsMap, icon, badgeClassName, testId, collapsible, onTaskComplete, onTaskEdit }: TaskGroupProps) {
  // Collapsible groups start collapsed so the list view opens as a compact overview
  // of the section headers/counts; non-collapsible groups are always expanded.
  const [isExpanded, setIsExpanded] = useState(!collapsible);

  if (tasks.length === 0) return null;

  const header = (
    <>
      {icon}
      <h2 className="text-sm font-semibold">{title}</h2>
      <Badge variant="outline" className={badgeClassName ?? 'text-[10px] h-5 px-1.5'}>
        {tasks.length}
      </Badge>
      {collapsible && (
        <ChevronDown
          className={cn('h-4 w-4 text-muted-foreground ml-auto transition-transform', !isExpanded && '-rotate-90')}
        />
      )}
    </>
  );

  return (
    <section className="border rounded-lg p-4 bg-card" data-testid={testId}>
      {collapsible ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          data-testid={`${testId}-toggle`}
          className="flex w-full items-center gap-2 h-auto p-0 mb-4 hover:bg-transparent justify-start"
        >
          {header}
        </Button>
      ) : (
        <div className="flex items-center gap-2 mb-4">{header}</div>
      )}
      {isExpanded && (
        <div className="space-y-3">
          {tasks.map((task) => (
            <TaskListItemDetailed
              key={task.id}
              task={task}
              claim={claimsMap.get(task.claimId)}
              onTaskComplete={onTaskComplete}
              onTaskEdit={onTaskEdit}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Full, non-day-scoped task list. Tasks are expected to already be sorted by due
 * date (see lib/task-filters.ts) and are grouped into Overdue / Upcoming / No Due
 * Date / Completed sections so users can scan priorities at a glance.
 */
export function TaskListView({ tasks, claimsMap, onTaskComplete, onTaskEdit }: TaskListViewProps) {
  if (tasks.length === 0) {
    return (
      <div className="border rounded-lg p-6 bg-card">
        <div className="text-center text-muted-foreground py-12">
          <p>No tasks match the current filters</p>
        </div>
      </div>
    );
  }

  // Cancelled tasks are grouped with completed ones (there's no separate
  // "Cancelled" section) — matches isTaskClosed in lib/task-filters.ts.
  const completedTasks = tasks.filter((task) => task.status === 'completed' || task.status === 'cancelled');
  const activeTasks = tasks.filter((task) => task.status !== 'completed' && task.status !== 'cancelled');
  const overdueTasks = activeTasks.filter(isTaskOverdue);
  const upcomingTasks = activeTasks.filter((task) => !!task.dueDate && !isTaskOverdue(task));
  const noDueDateTasks = activeTasks.filter((task) => !task.dueDate);

  return (
    <div className="space-y-6">
      <TaskGroup
        title="Overdue"
        tasks={overdueTasks}
        claimsMap={claimsMap}
        icon={<CalendarX2 className="h-4 w-4 text-red-500" />}
        badgeClassName="text-[10px] h-5 px-1.5 text-red-600 border-red-300 bg-red-50"
        testId="task-list-view-overdue"
        collapsible
        onTaskComplete={onTaskComplete}
        onTaskEdit={onTaskEdit}
      />
      <TaskGroup
        title="Upcoming"
        tasks={upcomingTasks}
        claimsMap={claimsMap}
        icon={<CalendarClock className="h-4 w-4 text-primary" />}
        testId="task-list-view-upcoming"
        collapsible
        onTaskComplete={onTaskComplete}
        onTaskEdit={onTaskEdit}
      />
      <TaskGroup
        title="No Due Date"
        tasks={noDueDateTasks}
        claimsMap={claimsMap}
        icon={<CalendarOff className="h-4 w-4 text-muted-foreground" />}
        testId="task-list-view-no-due-date"
        collapsible
        onTaskComplete={onTaskComplete}
        onTaskEdit={onTaskEdit}
      />
      <TaskGroup
        title="Completed"
        tasks={completedTasks}
        claimsMap={claimsMap}
        icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        badgeClassName="text-[10px] h-5 px-1.5 text-emerald-600 border-emerald-300 bg-emerald-50"
        testId="task-list-view-completed"
        collapsible
        onTaskComplete={onTaskComplete}
        onTaskEdit={onTaskEdit}
      />
    </div>
  );
}
